import { useState } from 'react'
import {
  Check,
  LayoutGrid,
  Send,
  Search,
  ChevronRight,
  MoreHorizontal,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Product Deck
   Two-column layout: copy left, glassmorphism mock UI right. Same branding.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function ProductDeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT & MOCK COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide {
  title: string
  component: React.FC
}

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

/** Two-column slide: left = copy, right = mock UI card(s) */
function SlideLayout({
  tag,
  headline,
  highlight,
  subline,
  bullets,
  roles,
  bottomLine,
  children,
}: {
  tag?: string
  headline: string
  highlight?: string
  subline?: string
  bullets?: string[]
  roles?: string[]
  bottomLine?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
          {headline}
          {highlight && <span className="text-[#FF2300] underline decoration-[#FF2300]/60 underline-offset-2">{highlight}</span>}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400">{subline}</p>}
        {bullets && (
          <ul className="mt-6 space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
                <span className="text-zinc-300">{b}</span>
              </li>
            ))}
          </ul>
        )}
        {roles && (
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-9 h-9 rounded-full bg-white/10 border border-white/10" />
            ))}
            <span className="text-zinc-500 mx-1">···</span>
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => (
                <span key={r} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[13px] text-zinc-300">
                  {r}
                </span>
              ))}
            </div>
          </div>
        )}
        {bottomLine && (
          <p className="mt-6 text-sm text-zinc-500 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500/80" />
            {bottomLine}
          </p>
        )}
      </div>
      <div className="relative flex items-center justify-center min-h-[320px] lg:min-h-[400px]">
        {children}
      </div>
    </div>
  )
}

/** Glassmorphism mock card with optional glow */
function MockCard({
  className = '',
  glow,
  children,
}: {
  className?: string
  glow?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-xl ${glow ? 'shadow-[0_0_60px_-12px_rgba(255,35,0,0.2)]' : ''} ${className}`}
      style={{ boxShadow: glow ? '0 0 60px -12px rgba(255,35,0,0.2)' : undefined }}
    >
      {children}
    </div>
  )
}

/* ── Mock: Hero overlapping windows ──────────────────────────────────────── */
function MockHeroOverlap() {
  return (
    <div className="relative w-full max-w-xl mx-auto flex items-center justify-center">
      <MockCard className="absolute right-0 top-0 w-[72%] max-w-md p-5 opacity-90" glow>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold text-white">Team Management</span>
          <LayoutGrid className="w-4 h-4 text-zinc-500" />
        </div>
        <button className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[13px] text-zinc-300 mb-4">
          Connect Google <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg bg-white/5 p-3">
            <div className="text-lg font-semibold text-white">42</div>
            <div className="text-[11px] text-zinc-500">Shows</div>
          </div>
          <div className="rounded-lg bg-white/5 p-3">
            <div className="text-lg font-semibold text-white">53</div>
            <div className="text-[11px] text-zinc-500">Tasks</div>
          </div>
        </div>
        <div className="space-y-2">
          {['Charly Manager', 'Promoter'].map((name, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
              <div className="w-8 h-8 rounded-full bg-white/10" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-white truncate">{name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{name.toLowerCase().replace(' ', '')}@spacecamp.la</div>
              </div>
              <span className="text-[10px] text-zinc-500">Manager</span>
            </div>
          ))}
        </div>
      </MockCard>
      <MockCard className="relative left-0 z-10 w-[68%] max-w-sm p-4 shadow-xl" glow>
        <div className="flex gap-4 mb-3 border-b border-white/10 pb-2">
          <span className="text-[13px] font-medium text-[#FF2300] border-b-2 border-[#FF2300] pb-0.5">Shows</span>
          <span className="text-[13px] text-zinc-500">Schedule</span>
        </div>
        <div className="text-[11px] text-zinc-500 space-y-1 mb-3">
          <div>UPCOMING · 31</div>
          <div>SHOWS · 19</div>
          <div>TASKS</div>
        </div>
        <div className="space-y-2">
          {['Charly...', 'charlymanager', 'promoter'].map((label, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5">
              <div className="w-6 h-6 rounded-full bg-white/10" />
              <span className="text-[12px] text-zinc-300 truncate">{label}</span>
            </div>
          ))}
        </div>
      </MockCard>
      <MockCard className="absolute right-4 bottom-0 w-[58%] max-w-xs p-4 z-20" glow>
        <div className="flex items-center gap-2 mb-3 rounded-lg bg-white/5 border border-white/10 px-2 py-1.5">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[12px] text-zinc-500">Ask anything...</span>
        </div>
        <div className="space-y-1.5">
          {['Shrine – 7,842 / 10,500 (75% sold)', 'Crew lands 5:30 PM', '+12 Playlists added', 'Final splits uploaded'].map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-[12px] text-zinc-300">
              <div className="w-4 h-4 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0">
                <Check className="w-2.5 h-2.5 text-emerald-400" />
              </div>
              {t}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5">
          <span className="text-[11px] text-zinc-500 flex-1">Ask Dizee...</span>
          <Send className="w-3.5 h-3.5 text-[#FF2300]" />
        </div>
      </MockCard>
    </div>
  )
}

/* ── Mock: Update Ticket Counts ─────────────────────────────────────────── */
function MockTicketCounts() {
  const rows = [
    { name: 'Tempo', loc: 'Philadelphia · Mar 20, 2026', cap: '400', count: '328', pct: 82, status: 'On Sale', saved: true },
    { name: 'Stage West', loc: 'State College · Mar 21, 2026', cap: '1,200', count: '915', pct: 76, status: 'Venue TBA', saved: true },
    { name: 'Pal Norte', loc: 'Monterrey · Mar 27, 2026', cap: 'TBA', count: '–', pct: 0, status: 'Capacity TBA', saved: false },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Update Ticket Counts</h3>
          <p className="text-[13px] text-zinc-500">19 Upcoming Shows</p>
        </div>
        <div className="flex gap-2">
          <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Import</button>
          <button className="rounded-lg bg-[#FF2300] px-3 py-1.5 text-[12px] font-medium text-white">Save All</button>
        </div>
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">{r.name}</span>
              <div className="flex items-center gap-2">
                {r.saved && <span className="text-[11px] text-emerald-500">✔ Saved</span>}
                {!r.saved && <span className="text-[11px] text-zinc-500">• Pending</span>}
                <span className="text-[10px] text-zinc-500 border border-white/10 rounded px-1.5">{r.status}</span>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mb-2">{r.loc}</p>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div><span className="text-zinc-500">Sellable Cap</span><div className="font-medium text-white">{r.cap}</div></div>
              <div><span className="text-zinc-500">Ticket Count</span><div className="font-medium text-white">{r.count}</div></div>
              <div><span className="text-zinc-500">% Sold</span><div className="font-medium text-[#FF2300]">{r.pct ? `${r.pct}%` : '–'}</div></div>
            </div>
            {r.pct > 0 && (
              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#FF2300]" style={{ width: `${r.pct}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Know What Needs Attention ────────────────────────────────────── */
function MockUpdateTickets() {
  const items = [
    { title: 'Tempo', warn: true, sub: 'Slowing down with 5 days left', cap: '400', count: '298', pct: 75 },
    { title: 'Stage West', sub: 'State College - Mar 21, 2026', cap: '1,200', count: '571', pct: 47, synced: true },
    { title: 'Pal Norte', sub: 'Monterrey - Mar 27, 2026', cap: 'TBA', count: '–', pct: 0, pending: true },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Update Tickets</h3>
          <p className="text-[13px] text-zinc-500">19 upcoming Shows</p>
        </div>
        <div className="flex gap-2">
          <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Import</button>
          <button className="rounded-lg bg-[#FF2300] px-3 py-1.5 text-[12px] font-medium text-white">Save All</button>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((r, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-medium text-white">{r.title}</span>
              {r.warn && <span className="text-[10px] text-amber-400 flex items-center gap-1">⚠ Warning</span>}
              {r.synced && <span className="text-[10px] text-emerald-500">✔ Synced</span>}
              {r.pending && <span className="text-[10px] text-zinc-500">• Pending</span>}
            </div>
            {r.sub && <p className="text-[11px] text-zinc-500 mb-2">{r.sub}</p>}
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div><span className="text-zinc-500">Sellable Cap</span><div className="font-medium text-white">{r.cap}</div></div>
              <div><span className="text-zinc-500">Ticket Count</span><div className="font-medium text-white">{r.count}</div></div>
              <div><span className="text-zinc-500">% Sold</span><div className="font-medium text-[#FF2300]">{r.pct ? `${r.pct}%` : '–'}</div></div>
            </div>
            {r.pct > 0 && (
              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#FF2300]" style={{ width: `${r.pct}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Team tab ─────────────────────────────────────────────────────── */
function MockTeamTab() {
  const members = [
    { name: 'Charly', email: 'charly@spacecamp.la', role: 'Owner' },
    { name: 'charlymanager', email: 'charlyteam@spacecamp.la', role: 'Manager' },
    { name: 'promoter', email: 'promoter@spacecamp.la', role: 'Manager' },
  ]
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-4 border-b border-white/10">
          <span className="text-[13px] text-zinc-500 pb-2">Schedule</span>
          <span className="text-[13px] text-zinc-500 pb-2">Shows</span>
          <span className="text-[13px] font-medium text-white border-b-2 border-[#FF2300] pb-2">Team</span>
        </div>
        <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Invite Team Member</button>
      </div>
      <div className="space-y-0 divide-y divide-white/5">
        {members.map((m, i) => (
          <div key={i} className="flex items-center gap-3 py-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-medium text-white">{m.name}</div>
              <div className="text-[12px] text-zinc-500">{m.email}</div>
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
              <span className="w-2 h-2 rounded-full bg-emerald-500/80" />
              {m.role}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
        <span className="text-[13px] text-zinc-500 flex-1">How&apos;d the show in Indy sell?</span>
        <button className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Send className="w-4 h-4 text-[#FF2300]" /></button>
      </div>
    </MockCard>
  )
}

/* ── Mock: Manage Tasks ────────────────────────────────────────────────── */
function MockManageTasks() {
  const tasks = [
    { tag: 'BRAND DEAL', title: 'Draft IG Story for Genius promotion', due: 'Due Tomorrow', status: 'In progress' },
    { tag: 'DSP PITCH', title: 'Pitch single to Spotify editors', due: 'Due Friday', status: 'Tomorrow' },
    { tag: 'MARKETING', title: 'Approve artwork v3', due: 'Apr 9, 2026', status: 'Not started' },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Manage Tasks</h3>
          <p className="text-[13px] text-zinc-500">6 Total Tasks</p>
        </div>
        <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400 flex items-center gap-1">Add Tasks ▾</button>
      </div>
      <div className="flex gap-2 mb-3 border-b border-white/10">
        {['ALL', 'EDIT', 'DSP', 'BRAND'].map((t, i) => (
          <span key={t} className={`text-[12px] pb-2 ${i === 3 ? 'text-white font-medium border-b-2 border-[#FF2300]' : 'text-zinc-500'}`}>{t}</span>
        ))}
      </div>
      <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 flex items-center gap-2 mb-3">
        <Search className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-[12px] text-zinc-500">Filter by keyword...</span>
      </div>
      <div className="space-y-2">
        {tasks.map((t, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3 flex items-center gap-3">
            <span className="text-[10px] font-medium bg-[#FF2300]/20 text-[#FF2300] px-2 py-0.5 rounded">{t.tag}</span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-white">{t.title}</div>
              <div className="text-[11px] text-zinc-500">{t.due}</div>
            </div>
            <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0" />
            <span className="text-[11px] text-zinc-500 flex items-center gap-1">
              {t.status === 'In progress' && <span className="w-1.5 h-1.5 rounded-full bg-[#FF2300]" />}
              {t.status}
            </span>
            <MoreHorizontal className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Ask Dizee / Key Insights ─────────────────────────────────────── */
function MockAskDizee() {
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <h3 className="text-base font-semibold text-white mb-3">Ask Dizee.</h3>
      <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2 mb-4">
        <Search className="w-4 h-4 text-zinc-500" />
        <span className="text-[13px] text-zinc-500 flex-1">Ask anything...</span>
        <Send className="w-4 h-4 text-[#FF2300]" />
      </div>
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-400" /></div>
          <div><div className="text-[13px] text-white">Shrine - 7,842 / 10,500 (75% sold)</div></div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-400" /></div>
          <div>
            <div className="text-[13px] text-white">Today&apos;s top three opening Spotify playlists</div>
            <div className="flex gap-1 mt-1">
              <span className="w-2 h-2 rounded-sm bg-[#FF2300]" /><span className="text-[11px] text-zinc-500">Mellow Mood</span>
              <span className="w-2 h-2 rounded-sm bg-amber-600" /><span className="text-[11px] text-zinc-500">Lo-Fi Beats</span>
              <span className="w-2 h-2 rounded-sm bg-zinc-600" /><span className="text-[11px] text-zinc-500">Young & Free</span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-semibold text-emerald-400">$</span></div>
          <div><div className="text-[13px] text-white font-semibold">$1,235.89 estimated weekly</div><div className="text-[11px] text-zinc-500">Royalties Sunday</div></div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-semibold text-emerald-400">S</span></div>
          <div><div className="text-[13px] text-white">Nashville: Thursday flights, first show Friday</div><div className="text-[11px] text-zinc-500">Due Apr 13, 2026</div></div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/10 rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2">
        <span className="text-[13px] text-zinc-500 flex-1">How&apos;d the show in Indy sell?</span>
        <Send className="w-4 h-4 text-[#FF2300]" />
      </div>
    </MockCard>
  )
}

/* ── Mock: Schedule / Calendar ───────────────────────────────────────────── */
function MockSchedule() {
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white">Schedule</h3>
        <button className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Connect Google <ChevronRight className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex gap-4 mb-4">
        <div className="text-3xl font-semibold text-white">31</div>
        <div className="text-3xl font-semibold text-white">19</div>
        <div className="text-3xl font-semibold text-zinc-500">12</div>
      </div>
      <div className="space-y-2 text-[13px]">
        <div className="text-zinc-400">LA to NY flights (Charly to approve)</div>
        <div className="text-zinc-400">Call Haley Monday</div>
        <div className="text-zinc-400">Task Reley Monday</div>
      </div>
    </MockCard>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-4 w-auto text-white" />
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] max-w-xl">
          Dizee makes it easy to manage a <span className="text-[#FF2300]">music career.</span>
        </h1>
        <p className="text-base sm:text-lg text-zinc-400 mt-6">Focus on making music. Leave the rest to us.</p>
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="w-10 h-10 rounded-full bg-white/10 border border-white/10" />
          ))}
          <span className="text-zinc-500 mx-1">···</span>
          <p className="text-[13px] text-zinc-500">Manager · Booking Agent · Artist · Promoter</p>
        </div>
        <div className="mt-10 flex gap-3">
          <a href="#problems" className="rounded-lg bg-[#FF2300] px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity">Learn more</a>
          <a href="#problems" className="rounded-lg border border-[#FF2300] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#FF2300]/10 transition-colors">Get started</a>
        </div>
      </div>
      <div className="relative flex items-center justify-center min-h-[340px] lg:min-h-[400px]">
        <MockHeroOverlap />
      </div>
    </div>
  )
}

function SlideProblems() {
  return (
    <SlideLayout
      tag="Problems we solve"
      headline="Communication and data that actually stay "
      highlight="in sync."
      subline="One source of truth. One place to look. No more version chaos."
      bullets={[
        'Seamless communication between internal and external teams (promoters, agents, labels).',
        'Ticket counts that stay current everywhere — never work from stale numbers.',
      ]}
      bottomLine="Live data across team"
    >
      <MockTicketCounts />
    </SlideLayout>
  )
}

function SlideSheets() {
  return (
    <SlideLayout
      tag="Google Sheets integration"
      headline="Your show data, bidirectional sync. "
      highlight="No workflow change."
      subline="We meet you where you already are. Connect once, and show data and ticket counts flow between Dizee and Google Sheets automatically."
      bullets={[
        'One sheet, many tabs — ticket tab syncs across everything.',
        '~2 minutes to set up. Bidirectional sync, no duplicate entry.',
      ]}
      bottomLine="Single glance, every morning."
    >
      <MockUpdateTickets />
    </SlideLayout>
  )
}

function SlideAccess() {
  return (
    <SlideLayout
      tag="Teams & access"
      headline="Everyone sees only "
      highlight="what they need."
      subline="Granular access so your team has a separate view for the things they work on only. Less noise, fewer mistakes."
      bullets={[
        'Shows only — tour dates, ticket counts, promoters.',
        'Music only — releases, catalog, credits.',
        'Full access — managers and admins see the full career view.',
      ]}
      roles={['Manager', 'Booking Agent', 'Artist', 'Promoter']}
      bottomLine="Everyone sees the same thing."
    >
      <MockTeamTab />
    </SlideLayout>
  )
}

function SlideCalendar() {
  return (
    <SlideLayout
      tag="Schedule & Google Calendar"
      headline="Your schedule, integrated with "
      highlight="Google Calendar."
      subline="Add items from Dizee into your Google Calendar, create a shared calendar, or pick specific calendars. See everything in one place."
      bullets={[
        'Add show releases, music releases, and tasks from Dizee to Google Calendar.',
        'Shared calendar for the whole team, or pick specific calendars.',
        'Toggle between “what’s on Google Calendar” and “everything on the artist’s career”.',
      ]}
      bottomLine="One schedule view: Dizee + Google — all in sync."
    >
      <MockSchedule />
    </SlideLayout>
  )
}

function SlideTasks() {
  return (
    <SlideLayout
      tag="Tasks"
      headline="Never miss key "
      highlight="deadlines."
      subline="Need your team to handle marketing tasks? Managers track it all here."
      bullets={[
        'Create and assign tasks.',
        'Approve brand deals.',
        'Stay ahead of pitch deadlines.',
      ]}
      bottomLine="Everyone knows what to do next."
    >
      <MockManageTasks />
    </SlideLayout>
  )
}

function SlideSummary() {
  return (
    <SlideLayout
      tag="Summary"
      headline="Dizee becomes the "
      highlight="source of truth"
      subline="Ask questions about: Shows, Music, Publishing, Travel, Marketing, Team. From asking your team to asking Dizee."
      bullets={[
        'Get key insights instantly — ticket sales, promo opportunities, royalties, travel.',
        'Answers for any question, in one place.',
      ]}
    >
      <MockAskDizee />
    </SlideLayout>
  )
}

function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="max-w-2xl">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug">
          The operating system and source of truth for modern music careers — without changing how you work.
        </p>
      </div>
      <div className="mt-12 space-y-2">
        <p className="text-[14px] text-[#FF2300] font-medium">www.dizee.com</p>
      </div>
    </div>
  )
}

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Problems we solve', component: SlideProblems },
  { title: 'Google Sheets', component: SlideSheets },
  { title: 'Teams & access', component: SlideAccess },
  { title: 'Schedule & Calendar', component: SlideCalendar },
  { title: 'Tasks', component: SlideTasks },
  { title: 'Summary', component: SlideSummary },
  { title: 'Closing', component: SlideClosing },
]
